<template>
    <transition name="fade">
        <div class="modal is-active" v-show="visible">
            <div class="modal-background" @click="destroy"></div>
            <div class="modal-content">
                <div class="popup-dialog">

                    <div class="popup-title">
                        <h1 class="title is-1">{{ this.formTitle }}</h1>

                        <div class="popup-close" @click.prevent="destroy">
                            <i class="icon">close</i>
                        </div>
                    </div>

                    <g-form :action="formEndpoint" :form="form" :method="formMethod" :before="formatData"
                            @success="formSuccess" message="Wijzigingen succesvol opgeslagen"
                            v-slot="{ activeLocale, changeLocale, availableLocales }"
                    >
                        <div class="popup-body popup-rows">
                            <!-- Locale selection for translatable fields -->
                            <div class="columns">
                                <div class="column is-one-quarter">
                                    <label>Taal</label>
                                </div>
                                <div class="column">
                                    <div class="field-body">
                                        <g-select
                                            name="locale"
                                            label="Taal"
                                            :value="activeLocale"
                                            @input="changeLocale"
                                            :options="availableLocales"
                                        />
                                    </div>
                                </div>
                            </div>

                            <!-- Wrapping name (only available on creation) -->
                            <div class="columns">
                                <div class="column is-one-quarter">
                                    <label>Naam</label>
                                </div>
                                <div class="column">
                                    <div class="field-body">
                                        <g-input name="name" label="Naam" :disabled="isCreateNewForm === false"/>
                                    </div>
                                </div>
                            </div>

                            <!-- Display name -->
                            <div class="columns">
                                <div class="column is-one-quarter">
                                    <label>Weergavenaam</label>
                                </div>
                                <div class="column">
                                    <div class="field-body">
                                        <g-input name="translations" label="Weergavenaam" translatable/>
                                    </div>
                                </div>
                            </div>

                            <!-- Description -->
                            <div class="columns">
                                <div class="column is-one-quarter">
                                    <label>Beschrijving</label>
                                </div>
                                <div class="column">
                                    <div class="field-body">
                                        <g-input type="textarea" name="description" label="Beschrijving" placeholder="Beschrijving van de sleeve..." translatable/>
                                    </div>
                                    <p class="help mt-2">Ondersteunt *dikgedrukt* en nieuwe regels voor opmaak.</p>
                                </div>
                            </div>

                            <!-- Highlights -->
                            <div class="columns">
                                <div class="column is-one-quarter">
                                    <label>Kenmerken</label>
                                </div>
                                <div class="column">
                                    <div class="field-body">
                                        <g-input type="textarea" name="features" label="Kenmerken" placeholder="Eén kenmerk per regel..." translatable/>
                                    </div>
                                    <p class="help mt-2">Bullet point kenmerkenlijst. Voer elk kenmerk op een nieuwe regel in.</p>
                                </div>
                            </div>

                            <!-- Badge -->
                            <div class="columns">
                                <div class="column is-one-quarter">
                                    <label>Badge</label>
                                </div>
                                <div class="column">
                                    <div class="field-body">
                                        <g-input name="badge" label="Badge" placeholder="bijv. Populair, Nieuw" translatable/>
                                    </div>
                                    <p class="help mt-2">Badge tekst voor de showcase weergave (bijv. "Nieuw").</p>
                                </div>
                            </div>

                            <!-- Price -->
                            <div class="columns">
                                <div class="column is-one-quarter">
                                    <label>Prijs</label>
                                </div>
                                <div class="column">
                                    <div class="field-body">
                                        <g-format ref="format" label="Consument" name="price" type="currency"/>
                                        <g-format ref="format" label="Bedrijf" name="company_price" type="currency"/>
                                    </div>
                                </div>
                            </div>

                            <!-- Thumbnail -->
                            <div class="columns">
                                <div class="column is-one-quarter">
                                    <label>Thumbnail</label>
                                </div>
                                <div class="column">
                                    <template v-if="form.thumbnail && in_edit_mode === false &&
                                                    isCreateNewForm === false &&
                                                    typeof form.thumbnail === 'string'">
                                        <div class="thumbnail-preview">
                                            <img :src="form.thumbnail" alt="Sleeve" class="sleeve-thumbnail">
                                        </div>
                                        <div class="mt-3">
                                            <g-button color="is-white"
                                                      @click="in_edit_mode = true"
                                                      :is-rounded="false">Wijzigen
                                            </g-button>
                                        </div>
                                    </template>
                                    <template v-else>
                                        <div class="field-body">
                                            <g-file name="thumbnail" accept="image/png, image/jpeg"/>
                                        </div>
                                    </template>
                                </div>
                            </div>

                            <!-- Company specific -->
                            <div class="columns">
                                <div class="column is-one-quarter">
                                    <label>Koppelen aan bedrijven</label>
                                </div>
                                <div class="column">
                                    <div class="field-body">
                                        <tree-select :multiple="true"
                                                     :async="true"
                                                     :load-options="loadOptions"
                                                     placeholder="Begin met typen om een bedrijf te vinden.."
                                                     :value="[{ id: 1, name: 'Gifty B.V.' }]"
                                                     :defaultOptions="defaultOptions"
                                                     loadingText="Aan het zoeken.."
                                                     searchPromptText="Begin met typen om een bedrijf te vinden.."
                                                     noResultsText="Geen resultaten.."
                                                     :clearable="false"
                                                     v-model="selectedCompanies"/>
                                    </div>
                                </div>
                            </div>

                            <!-- Minimal packaging type -->
                            <div class="columns">
                                <div class="column is-one-quarter">
                                    <label>Minimale verpakking</label>
                                </div>
                                <div class="column">
                                    <div class="field-body">
                                        <g-select name="package_type"
                                                  label="Minimale verpakking"
                                                  :options="packageTypeOptions"/>
                                    </div>
                                    <p class="help mt-2">Beperkt de beschikbare verzendmethoden. Geen: standaard
                                        briefpost en traceerbaar. Brievenbuspakket/Pakket: traceerbaar, avond- en
                                        same-day levering (geen standaard briefpost, bijv. voor grote sleeves of
                                        cadeauverpakkingen met extra inhoud).</p>
                                </div>
                            </div>

                            <!-- Show gift card preview together with thumbnail -->
                            <div class="columns">
                                <div class="column is-one-quarter">
                                    <label>Toon cadeaubon</label>
                                </div>
                                <div class="column">
                                    <div class="field-body">
                                        <g-switch name="show_giftcard"/>
                                    </div>
                                    <p class="help mt-2">Indien ingeschakeld wordt de cadeaubon-afbeelding onder de
                                        sleeve-afbeelding getoond in de bestelmodule. Indien uitgeschakeld is alleen de
                                        geüploade sleeve-afbeelding zichtbaar.</p>
                                </div>
                            </div>

                            <!-- Highlighted -->
                            <div class="columns">
                                <div class="column is-one-quarter">
                                    <label>Uitgelicht</label>
                                </div>
                                <div class="column">
                                    <div class="field-body">
                                        <g-switch name="is_highlighted"/>
                                    </div>
                                    <p class="help mt-2">Markeer deze sleeve als uitgelicht in de showcase weergave.</p>
                                </div>
                            </div>

                            <!-- Prioritize (display on top) -->
                            <div class="columns">
                                <div class="column is-one-quarter">
                                    <label>Prioriteit</label>
                                </div>
                                <div class="column">
                                    <div class="field-body">
                                        <g-switch name="priority"/>
                                    </div>
                                    <p class="help mt-2 d-block">Indien ingeschakeld wordt deze sleeve bovenaan de
                                        lijst weergegeven in de bestelmodule.</p>
                                </div>
                            </div>

                            <!-- Active -->
                            <div class="columns">
                                <div class="column is-one-quarter">
                                    <label>Actief</label>
                                </div>
                                <div class="column is-vcentered">
                                    <div class="field-body">
                                        <g-switch name="active"/>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="popup-footer">
                            <a @click.prevent="destroy" href="#" role="button">Annuleren</a>
                            <g-button>Opslaan</g-button>
                        </div>
                    </g-form>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>

export default {
    props: {
        id: Number
    },

    data() {
        const object = this.$store.getters['adminWrappingManagement/getById'](this.id)
        return {
            selectedCompanies: [],
            defaultOptions: [],
            object: object,
            form: new Form({
                locale: this.$i18n.locale,
                name: null,
                price: '2,50',
                company_price: '1,75',
                priority: null,
                active: null,
                show_giftcard: 1,
                package_type: null,
                is_highlighted: 0,
                translations: {
                    translatable: true,
                    useFallback: true,
                    default: {
                        nl: '',
                        en: ''
                    },
                },
                description: {
                    translatable: true,
                    useFallback: true,
                    default: {
                        nl: '',
                        en: ''
                    },
                },
                features: {
                    translatable: true,
                    useFallback: true,
                    default: {
                        nl: '',
                        en: ''
                    },
                },
                badge: {
                    translatable: true,
                    useFallback: true,
                    default: {
                        nl: '',
                        en: ''
                    },
                },
                thumbnail: null,
                connected_to: [],
            }, {
                locales: this.$store.getters['application/availableLocales']
            }),
            in_edit_mode: false,
        }
    },

    computed: {
        isCreateNewForm() {
            return this.object == null
        },

        formTitle() {
            return this.isCreateNewForm ? 'Sleeve toevoegen' : 'Sleeve aanpassen'
        },

        formEndpoint() {
            let endpoint = `/api/admin/wrapping-management`

            if (false === this.isCreateNewForm) {
                endpoint += `/${this.id}`
            }

            return endpoint
        },

        formMethod() {
            return this.isCreateNewForm ? 'post' : 'patch'
        },

        packageTypeOptions() {
            return [
                {name: 'Geen restricties', value: null},
                {name: 'Brievenbuspakket', value: 'letterbox_parcel'},
                {name: 'Pakket', value: 'parcel'}
            ]
        }
    },

    created() {
        // Populate form fields when editing
        if (this.isCreateNewForm === false) {
            this.form.setFormData({
                data: this.object
            })
        }

        // Format price fields
        if (this.form.price) {
            this.form.price = this.object ? parseInt(this.object.price) / 100 : this.form.price
            this.form.company_price = this.object ? parseInt(this.object.company_price) / 100 : this.form.company_price
        }

        // Populate connected companies when editing
        if (false === this.isCreateNewForm && this.object.connected_to.length >= 1) {
            this.defaultOptions = this.object.connected_to.map((company) => {
                return {
                    id: company.id,
                    label: `#${company.id} - ${company.label}`
                }
            })

            this.selectedCompanies = this.object.connected_to.map((company) => {
                return company.id
            })
        }
    },

    methods: {
        loadOptions({action, searchQuery, callback}) {
            if (action === "ASYNC_SEARCH") {
                this.$store.dispatch('adminWrappingManagement/findWrapping', searchQuery).then(response => {
                    const results = response.data.map((company) => {
                        return {
                            id: company.id,
                            label: `#${company.id} - ${company.name}`
                        }
                    })

                    callback(null, results)
                })
            }
        },

        formSuccess(response) {
            const action = this.isCreateNewForm ? 'adminWrappingManagement/add' : 'adminWrappingManagement/update'

            this.$store.dispatch(action, {
                object: response.data
            }).then(this.destroy)
        },

        formatData(form) {
            form.connected_to = this.selectedCompanies

            if (typeof form.thumbnail === 'string') {
                delete form.thumbnail
            }

            return form
        },
    },
}
</script>

<style lang="scss" scoped>
.thumbnail-preview {
    .sleeve-thumbnail {
        max-width: 183px;
        max-height: 130px;
        border-radius: 4px;
        border: 1px solid #e0e0e0;
    }
}
</style>
